import { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  useMediaQuery,
  useTheme,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Grid,
  Theme,
  FormHelperText
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { Check, Error, Warning } from '@mui/icons-material';
import { CustomButton } from '@buttons/CustomButton';
import { useStyles } from './useStyle';
import { useDriverStore } from '@store/driverInfoStore';
import { calculateAge } from '@utils/utilityFunctions';
import { getCPById } from '@api/getCP';
import { CPCustomMask } from '@inputCustomMasks/CPCustomMask';
import { LocalizationProvider, DatePicker } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { es } from 'date-fns/locale';
import ReactTooltip from 'react-tooltip';
import { formatDate } from '@utils/formatDate';
import { useTokenStore } from '@store/TokenStore';
import { usePolicyStore } from '@store/policyStore';
import { useCarStore } from '@store/carInfoStore';
import TagManager from 'react-gtm-module';

interface Props {
  padding?: number;
  elevation?: number;
  isAbsolute: boolean;
  setIsNewQuotation?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsHeroAction?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFormInformation?: React.Dispatch<React.SetStateAction<boolean>>;
}

const useStyle = makeStyles((theme: Theme) => ({
  aseguradotooltip: {
    width: '30%',
  },
}));

export const NewQuotation = ({
  elevation,
  isAbsolute,
  setIsNewQuotation,
  setIsHeroAction,
  setIsFormInformation,
}: Props) => {
  const { driver, setDriver } = useDriverStore();
  const { breakpoints } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up('lg'));
  const isTablet = useMediaQuery(breakpoints.down('md'));
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const isMedium = useMediaQuery('(max-width:1100px)');
  const classes1 = useStyle();
  const classes = useStyles({
    isMobile,
    isTablet,
    isDesktop,
    isAbsolute,
  });
  const [zip, setZip] = useState<string>(driver.codigoPostal || '');
  const [warningZip, setWarningZip] = useState<{
    msg: string;
    isWarning: boolean;
  }>({
    msg: '',
    isWarning: false,
  });
  const [errorZip, setErrorZip] = useState<{
    msg: string;
    isError: boolean;
    isCorrect: boolean;
  }>({
    msg: '',
    isError: false,
    isCorrect: false,
  });
  const [errorGender, setErrorGender] = useState<{
    msg: string;
    isError: boolean;
    isCorrect: boolean;
  }>({
    msg: '',
    isError: false,
    isCorrect: false,
  });
  const [birthDate, setBirthDate] = useState<Date | null>(
    driver.fechaNacimiento || new Date()
  );
  const [errorAge, setErrorAge] = useState<{ msg: string; isError: boolean }>({
    msg: '',
    isError: false,
  });
  const { token } = useTokenStore();
  const [colorM, setColorM] = useState<string>('transparent');
  const [colorF, setColorF] = useState<string>('#F5FBFD');
  const [anterior, setAnterior] = useState<string>('');
  const [backM, setBackM] = useState<string>('#424242');
  const [backF, setBackF] = useState<string>('#039ECC');
  const [gender, setGender] = useState<string>('');
  const [name, setName] = useState('');
  const [errorName, setErrorName] = useState({
    msg: '',
    isError: false,
    isCorrect: false,
  });
  const [warningName, setWarningName] = useState({
    msg: '',
    isWarning: false,
  });
  const [limit, setLimit] = useState<any>({
    MaxAgeAcept: new Date(),
    MinAgeAcept: new Date(),
  });
  const policy = usePolicyStore();
  const car = useCarStore();

  const DataLayer = {
    event: 'CSform_step_1_view',
    eventCategory: 'Formulario.Seguro.Auto.v2',
    eventAction: 'step_1_view',
    eventLabel: 'nuevaCotización',
    telefono: undefined,
    correo: undefined,
    noCotizacion: policy.policy.policyNumber,
    modelo: car.car.description,
    marca: car.car.assembler,
    anio: car.car.model,
    paquete: undefined,
    forma_pago: undefined,
    aseguradora: undefined,
    tarjeta: undefined,
    tipo_tarjeta: undefined,
    tipo_vehiculo: undefined,
    colaborador: undefined,
    cupon: undefined,
  };

  const tagManagerArgs = {
    dataLayer: DataLayer,
  };

  const SetDataLayer = {
    event: 'CSform_step_1_submit',
    eventCategory: 'Formulario.Seguro.Auto.v2',
    eventAction: 'step_1_submit',
    eventLabel: 'nuevaCotización',
    telefono: undefined,
    correo: undefined,
    noCotizacion: policy.policy.policyNumber,
    modelo: car.car.description,
    marca: car.car.assembler,
    anio: car.car.model,
    paquete: undefined,
    forma_pago: undefined,
    aseguradora: undefined,
    tarjeta: undefined,
    tipo_tarjeta: undefined,
    tipo_vehiculo: undefined,
    colaborador: undefined,
    cupon: undefined,
  };

  const setTagManagerArgs = {
    dataLayer: SetDataLayer,
  };

  useEffect(() => {
    TagManager.dataLayer(tagManagerArgs);
    setDriver({
      ...driver,
      name,
      genero: gender
    });
  }, [name]);

  useEffect(() => {
    if (driver.genero === '') {
      setDriver({
        ...driver,
        genero: gender,
      });
    }

    if (gender === 'Hombre') {
      setColorM('#F5FBFD');
      setColorF('transparent');
      setBackM('#039ECC');
      setBackF('#424242');
    } else if(gender === 'Mujer') {
      setColorF('#F5FBFD');
      setColorM('transparent');
      setBackM('#424242');
      setBackF('#039ECC');
    } else {
      setColorF('transparent');
      setBackF('#424242');
      setColorM('transparent');
      setBackM('#424242');
    }

    calcYear();
  }, []);

  const calcYear = () => {
    const MaxAgeAcept = 99;
    const MinAgeAcept = 18;
    const currentYear = new Date().getFullYear();
    setLimit({
      MaxAgeAcept: new Date(`${currentYear - MinAgeAcept}-08-18`),
      MinAgeAcept: new Date(`${currentYear - MaxAgeAcept}-08-18`),
    });
  };

  const handleChangeGender = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    if (newAlignment !== null && anterior !== null) {
      setAnterior(newAlignment);
    } else if (anterior !== null) {
      newAlignment = anterior;
    } else {
      newAlignment = 'Mujer';
    }
    setGender(newAlignment);
    if (newAlignment === 'Hombre' || newAlignment === null) {
      setColorM('#F5FBFD');
      setColorF('transparent');
      setBackM('#039ECC');
      setBackF('#424242');
    } else {
      setColorF('#F5FBFD');
      setColorM('transparent');
      setBackM('#424242');
      setBackF('#039ECC');
    }
    setDriver({
      ...driver,
      genero: newAlignment,
    });
    setErrorGender({isError: false, isCorrect: true, msg: ''})
  };

  const handleChangeDriverBirthday = (newValue: Date | null) => {
    setBirthDate(newValue);
    calculateAge(formatDate(birthDate)) < 18 ||
    calculateAge(formatDate(birthDate)) > 99
      ? setErrorAge({
          msg: 'Por favor ingresar una edad valida mayor a 17 años y menor o igual a 99 años',
          isError: true,
        })
      : setErrorAge({
          msg: '',
          isError: false,
        });
  };

  const handleInputChange = (event: any) => {
    let regex = /^[a-zA-Z ]*$/;
    if (event.target.id === 'name') {
      if (event.target.value.trim() === '') {
        setErrorName({
          msg: '¡Oh, no! Te falta llenar este campo para poder continuar.',
          isError: true,
          isCorrect: false,
        });
        setName('');
        return;
      }
      if (regex.test(event.target.value)) {
        const nameText = event.target.value;
        if (nameText.length <= 30) {
          if (nameText.length < 2) {
            setWarningName({
              msg: '¡Oops! Parece que te faltan caracteres. El mínimo de caracteres es de 2. ',
              isWarning: true,
            });
          } else {
            setWarningName({
              msg: '',
              isWarning: false,
            });
          }
          setName(nameText.trim());
        } else {
          setWarningName({
            msg: '¡Oops! Parece que estás superando el máximo de caracteres. Sólo puedes escribir 30 caracteres.',
            isWarning: true,
          });
        }
        setErrorName({
          msg: '',
          isError: false,
          isCorrect: true,
        });
      } else {
        setErrorName({
          msg: '¡Oops! Aquí sólo puedes escribir letras.',
          isError: true,
          isCorrect: false,
        });
        setWarningName({
          msg: '',
          isWarning: false,
        });
      }
      return;
    }
  };

  const handleNextStep = async () => {
    TagManager.dataLayer(setTagManagerArgs);

    let isError = false;
    const data = await getCPById(zip, token);

    if (data.errors) {
      setErrorZip({
        msg: '¡Oops! Parece que hay un error en tu Código Postal.',
        isError: true,
        isCorrect: false,
      });
      isError = true;
    }

    if (driver.name === '' || name === '') {
      setErrorName({
        msg: '¡Oh, no! Te falta llenar este campo para poder continuar.',
        isError: true,
        isCorrect: false,
      });
      setWarningName({
        msg: '',
        isWarning: false,
      });
      isError = true;
    }
    if (driver.codigoPostal === '' || zip === '') {
      setErrorZip({
        msg: '!Oh, no! Te falta llenar este campo para poder continuar',
        isError: true,
        isCorrect: false,
      });
      isError = true;
    }
    if (driver.genero === '') {
      setErrorGender({isError: true, isCorrect: false, msg: '¡Oh, no! Te hace falta seleccionar un valor para poder continuar.'})
      isError = true
    }

    if (
      calculateAge(formatDate(birthDate)) < 18 ||
      calculateAge(formatDate(birthDate)) > 99
    ) {
      setErrorAge({
        msg: 'Por favor ingresar una edad valida mayor a 17 años y menor o igual a 99 años',
        isError: true,
      });

      isError = true;
    } else {
      setDriver({
        ...driver,
        fechaNacimiento: formatDate(birthDate),
      });
    }

    if (isError) {
      return;
    } else {
      setErrorName({
        msg: '',
        isError: false,
        isCorrect: true,
      });
      setWarningName({
        msg: '',
        isWarning: false,
      });
      setErrorAge({
        msg: '',
        isError: false,
      });

      setIsFormInformation && setIsFormInformation(true);
    }
  };

  const colorNameAdornment = () => {
    if (warningName.isWarning) {
      return 'warning';
    }

    if (errorName.isError) {
      return 'error';
    }

    return 'success';
  };

  const colorHelperText = () => {
    if (warningName.isWarning) {
      return '#FF9E1B';
    }

    if (errorName.isError) {
      return '#f44336';
    }

    return '#5AB52F';
  };

  const nameAdornment = () => {
    if (warningName.isWarning) {
      return <Warning color={'warning'} />;
    }

    if (errorName.isError) {
      return <Error color={'error'} />;
    }

    if (errorName.isCorrect) {
      <Check color={'success'} />;
    }

    return null;
  };

  const colorHelperTextCP = () => {
    if (warningZip.isWarning) {
      return '#FF9E1B';
    }

    if (errorZip.isError) {
      return '#f44336';
    }

    return '#5AB52F';
  };

  const cpRighIcon = () => {
    if (warningZip.isWarning) {
      return <Warning color={'warning'} />;
    }

    if (errorZip.isError) {
      return <Error color={'error'} />;
    }

    if (errorZip.isCorrect) {
      return <Check color={'success'} />;
    }

    return null;
  };

  const colorCPAdornment = () => {
    if (warningZip.isWarning) {
      return 'warning';
    }

    if (errorZip.isError) {
      return 'error';
    }

    return 'success';
  };

  return (
    <Box
      sx={{
        backgroundColor: '#F2FAFC',
        padding: isMobile ? '28px' : isMedium ? '72px' : '55.5px 150px',
      }}
    >
      <Paper
        elevation={elevation}
        style={{
          borderRadius: 8,
          maxWidth: '1140px',
          margin: 'auto',
        }}
      >
        <Grid
          container
          rowSpacing={3}
          paddingY={3}
          paddingX={isMobile ? '16px' : isTablet ? '47.5px' : '97px'}
        >
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <label
                className={classes.firstText}
                style={{
                  fontSize: !isTablet ? '24px' : '22px',
                  lineHeight: '22px',
                  marginBottom: isMobile ? 0 : 10,
                  textAlign: 'center',
                }}
              >
                Ahora, los datos del conductor asegurado
              </label>
            </Box>
          </Grid>
          <Grid
            item
            container
            rowGap={3}
            columnSpacing={3}
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
          >
            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
              <label className={classes.label}>¿Cómo se llama?</label>
              <TextField
                sx={{ marginTop: '20px' }}
                id="name"
                fullWidth
                label="Sólo su nombre"
                variant="outlined"
                placeholder="Nombre"
                onChange={handleInputChange}
                onBlur={(e) => {
                  if (e.target.value === '') {
                    setErrorName({
                      msg: '¡Oh, no! Te hace falta llenar este campo para poder continuar.',
                      isError: true,
                      isCorrect: false,
                    });
                  }
                }}
                error={errorName.isError}
                helperText={
                  <label style={{ color: colorHelperText() }}>
                    {errorName.msg || warningName.msg}
                  </label>
                }
                color={colorNameAdornment()}
                InputProps={{
                  style: {
                    borderRadius: 8,
                  },
                  endAdornment: nameAdornment(),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <label className={classes.label}>Sexo de nacimiento</label>
              </Box>
              <ToggleButtonGroup
                fullWidth
                sx={{
                  marginTop: '13px',
                  height: 56,
                  backgroundColor: '#F4F4F4',
                  border: errorGender.isError ? '1px solid red' : ''
                }}
                exclusive
                size={'medium'}
                value={gender}
                defaultValue={''}
                onChange={handleChangeGender}
                style={{ borderRadius: 8 }}
              >
                <ToggleButton
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#fafafa',
                  }}
                  value="Mujer"
                  style={{
                    fontWeight: 600,
                    borderRadius: 8,
                    border: 'none',
                    textTransform: 'none',
                    color: backF,
                    backgroundColor: colorF,
                  }}
                >
                  Mujer
                </ToggleButton>
                <ToggleButton value="" defaultChecked sx={{ display: 'none' }}></ToggleButton>
                <ToggleButton
                  value="Hombre"
                  style={{
                    fontWeight: 600,
                    borderRadius: 8,
                    border: 'none',
                    textTransform: 'none',
                    color: backM,
                    backgroundColor: colorM,
                  }}
                >
                  Hombre
                </ToggleButton>
              </ToggleButtonGroup>
              <FormHelperText error={true}>{errorGender.msg}</FormHelperText>
            </Grid>
          </Grid>
          <Grid
            item
            container
            rowGap={3}
            columnSpacing={3}
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
          >
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={6}
              xl={6}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <label className={classes.label} style={{ marginBottom: '20px' }}>
                ¿Cúal es su fecha de nacimiento?
              </label>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                <DatePicker
                  maxDate={limit.MaxAgeAcept}
                  minDate={limit.MinAgeAcept}
                  InputProps={{
                    style: {
                      borderRadius: 8,
                    },
                  }}
                  openTo="year"
                  views={['year', 'month', 'day']}
                  label="DD/MM/AAAA*"
                  inputFormat="dd/MM/yyyy"
                  value={new Date(`${birthDate}`)}
                  onChange={handleChangeDriverBirthday}
                  renderInput={(params) => (
                    <TextField
                      onBlur={() => {
                        calculateAge(formatDate(birthDate)) < 18 ||
                        calculateAge(formatDate(birthDate)) > 99
                          ? setErrorAge({
                              msg: 'Por favor ingresar una edad valida mayor a 17 años y menor o igual a 99 años',
                              isError: true,
                            })
                          : setErrorAge({
                              msg: '',
                              isError: false,
                            });
                      }}
                      {...params}
                      fullWidth
                      error={errorAge.isError}
                      helperText={errorAge.msg}
                      sx={{ borderRadius: 8 }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <label className={classes.label}>
                  ¿Cuál es su Código Postal?
                </label>
                <Info
                  color="primary"
                  data-for="cp"
                  data-tip=""
                  data-iscapture="true"
                  sx={{
                    cursor: 'pointer',
                    alignSelf: 'flex-end',
                  }}
                />
              </Box>

              <ReactTooltip
                id="cp"
                type="light"
                place="left"
                effect="solid"
                backgroundColor="#EBF8FB"
                className={classes1.aseguradotooltip}
              >
                <Box
                  padding={2}
                  marginTop={2}
                  borderRadius={2}
                  style={{ backgroundColor: '#EBF8FB' }}
                >
                  <Typography
                    textAlign={'start'}
                    fontWeight={'bold'}
                    fontSize={16}
                  >
                    CP
                  </Typography>
                  <Typography
                    textAlign={'start'}
                    fontWeight={'normal'}
                    fontSize={13}
                  >
                    No olvides que debe ser donde pasa la noche regularmente tu
                    coche, este dato es necesario para calcular tu tarifa.
                  </Typography>
                </Box>
              </ReactTooltip>
              <TextField
                sx={{ marginTop: '20px' }}
                value={zip}
                onBlur={(e) => {
                  if (e.target.value === '') {
                    setErrorZip({
                      msg: '¡Oh, no! Te hace falta llenar este campo para poder continuar.',
                      isError: true,
                      isCorrect: false,
                    });
                  } else {
                    setErrorZip({
                      msg: '',
                      isError: false,
                      isCorrect: false,
                    });
                  }
                }}
                onChange={(e) => {
                  setZip(e.target.value);
                  setDriver({
                    ...driver,
                    codigoPostal: e.target.value,
                  });

                  if (e.target.value.length === 4) {
                    setWarningZip({
                      msg: '¿Tu Código Postal tiene menos de 5 números? Agrega un 0 a la izquierda. Ejemplo: 01000',
                      isWarning: true,
                    });
                    setErrorZip({ msg: '', isError: false, isCorrect: false });
                    return;
                  }
                  setWarningZip({ msg: '', isWarning: false });
                }}
                fullWidth
                label="Código postal*"
                variant="outlined"
                maxRows={4}
                color={colorCPAdornment()}
                error={errorZip.isError}
                helperText={
                  <label style={{ color: colorHelperTextCP() }}>
                    {errorZip.msg || warningZip.msg}
                  </label>
                }
                InputProps={{
                  inputComponent: CPCustomMask as any,
                  style: {
                    borderRadius: 8,
                  },
                  endAdornment: cpRighIcon(),
                }}
              />
            </Grid>
          </Grid>
          <Grid
            item
            container
            rowGap={3}
            columnSpacing={3}
            justifyContent={'flex-end'}
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
          >
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={3.5}
              xl={3.5}
              display={'flex'}
              justifyContent={'flex-end'}
            >
              <CustomButton
                text={'Regresar'}
                color={'primary'}
                variant={'outlined'}
                style={{
                  width: isDesktop ? '266px' : '100%',
                  height: 56,
                  textTransform: 'none',
                }}
                onClick={() => {
                  setIsNewQuotation && setIsNewQuotation(false);
                  setIsHeroAction && setIsHeroAction(true);
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={3.5}
              xl={3.5}
              display={'flex'}
              justifyContent={'flex-end'}
            >
              <CustomButton
                text={'Continuar'}
                color={'primary'}
                variant={'contained'}
                style={{
                  width: isDesktop ? '266px' : '100%',
                  height: 56,
                  textTransform: 'none',
                }}
                onClick={handleNextStep}
              />
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
